import { Box, Drawer, IconButton, useMediaQuery } from '@mui/material';
import MapIcon from '../../assets/images/MapIcon.png';
import HelpIcon from '../../assets/images/HelpIcon.png';
import PersonIcon from '@mui/icons-material/Person';
import { ReactComponent as SidebarExpandIcon } from '../../assets/images/SidebarExpand.svg';
import LogoutIcon from '@mui/icons-material/Logout';
import MapIconSelected from '../../assets/images/MapIconSelected.png';
import HelpIconSelected from '../../assets/images/HelpIconSelected.png';
import RepertoireIcon from '../../assets/images/RepertoireIcon.png';
import RepertoireIconSelected from '../../assets/images/RepertoireIconSelected.png';
import { useNavigate } from 'react-router-dom';
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import { useState } from 'react';
import { useAuthWrapperContext } from 'Contexts/AuthWrapperContext';
import LogOutButton from '../../Components/LogOutButton';
import Fullscreen from '../../Components/Fullscreen';
import { isCapacitorNative } from 'Utils/Constants';
import { FeatureFlag, useFeatureFlag } from 'Utils/FeatureFlags';
import './Menu.css';

interface MenuProps {
  selected: string;
  /**
   * When true, the sidebar collapses into a slidable drawer on narrower
   * viewports (≤834px — iPad mini/Air/A16/11″ Pro in portrait and smaller), not
   * just very small phone screens. Lets pages (e.g. the Repertoire browser)
   * reclaim horizontal space. Defaults to the small-phone-only drawer behavior.
   */
  collapseToDrawer?: boolean;
  /**
   * Force the drawer regardless of viewport size. Used by the roadmap, which
   * routes its whole compact/phone experience by DEVICE CLASS (phone -> sheet +
   * drawer) rather than viewport width, so the menu must collapse whenever the
   * host page is in its phone/compact mode - including a phone in the
   * landscape-locked roadmap that is wider than the width-based `collapseToDrawer`
   * threshold. Defaults false, so every other caller is unaffected.
   */
  forceDrawer?: boolean;
  mobileOpen?: boolean;
  setMobileOpen?: (open: boolean) => void;
  hideMobileTrigger?: boolean;
}

const smallMobileMenuQuery = '(max-width:480px), (max-height:480px)';
// 834px is the 11″ iPad Pro/Air portrait width — the widest of the common iPads
// in portrait (mini 744, A16 820, 11″ 834). The 12.9″ Pro (1024) and every
// landscape iPad (≥1133) stay above it, so they keep the fixed sidebar.
const collapseToDrawerQuery = '(max-width:834px)';

/**
 * Whether the Menu renders as a slidable drawer rather than a fixed sidebar.
 * Always a drawer on small phone screens; additionally a drawer on viewports
 * ≤834px wide (iPad portrait through 11″, and narrower) when `collapseToDrawer`
 * is set. Exported so a host page can mirror the decision for its own layout.
 */
export const useMenuIsDrawer = (collapseToDrawer = false, forceDrawer = false): boolean => {
  const isSmallMobile = useMediaQuery(smallMobileMenuQuery);
  const isNarrow = useMediaQuery(collapseToDrawerQuery);
  return forceDrawer || isSmallMobile || (collapseToDrawer && isNarrow);
};

export const Menu = (props: MenuProps) => {
  const navigate = useNavigate();
  const showHistoryOfPianoMusic = useFeatureFlag(FeatureFlag.USE_HISTORY_OF_PIANO_MUSIC);
  const isDrawer = useMenuIsDrawer(props.collapseToDrawer, props.forceDrawer);
  const navMenu = (path: string) => {
    if (path === 'faq') {
      window.open('https://museflow.ai/faq', '_blank');
    } else {
      navigate(`/${path}`);
    }
  };
  const selected = props.selected;

  if (isDrawer) {
    return (
      <MobileMenuDrawer
        selected={selected}
        mobileOpen={props.mobileOpen}
        setMobileOpen={props.setMobileOpen}
        hideMobileTrigger={props.hideMobileTrigger}
      />
    );
  }

  return (
    <Box className="menu-container-styles">
      <Box
        className="menu-sidebar-fixed"
        sx={{
          backgroundColor: '#1D2F44',
          height: '100%',
          width: 'calc(var(--app-sidebar-width, 100px) - env(safe-area-inset-left))',
          position: 'fixed',
          top: '0px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Box
          className="menu-logo-wrap"
          sx={{
            marginTop: '30px',
            marginLeft: '4px',
            marginBottom: '10px',
            maxWidth: '100%',
            '&:hover': { cursor: 'pointer' },
          }}
          onClick={() => navigate('/roadmap')}
        >
          <MenuLogoImg />
        </Box>
        <Box className="menu-nav-scrollable">
          {!isCapacitorNative() &&
            (process.env.REACT_APP_NODE_ENV === 'local' ||
              process.env.REACT_APP_NODE_ENV === 'dev') && (
              <Box
                sx={{
                  '& h6': {
                    color: 'white',
                    margin: 0,
                  },
                }}
              >
                <h6>commit owner:</h6>
                <h6 style={{ marginBottom: '5px', color: 'gray' }}>
                  {process.env.REACT_APP_LAST_COMMIT_OWNER}
                </h6>
                <h6>branch:</h6>
                <h6 style={{ marginBottom: '5px', color: 'gray' }}>
                  {process.env.REACT_APP_BRANCH}
                </h6>
                <h6>commit:</h6>
                <h6 style={{ color: 'gray' }}>{process.env.REACT_APP_LAST_COMMIT}</h6>
              </Box>
            )}

          <Box
            component="button"
            type="button"
            onClick={() => navMenu('roadmap')}
            className="menu-hover-box"
            aria-current={selected == 'Roadmap' ? 'page' : undefined}
          >
            {selected == 'Roadmap' ? (
              <img className="menu-img-styles" src={MapIconSelected} />
            ) : (
              <img className="menu-img-styles" src={MapIcon} />
            )}
            <Box component="span" className="menu-text-styles">
              Roadmap
            </Box>
          </Box>

          <>
            <Box
              component="button"
              type="button"
              onClick={() => navMenu('repertoire')}
              className="menu-hover-box"
              aria-current={selected == 'Repertoire' ? 'page' : undefined}
            >
              {selected == 'Repertoire' ? (
                <img className="menu-img-styles" src={RepertoireIconSelected} />
              ) : (
                <img className="menu-img-styles" src={RepertoireIcon} />
              )}
              <Box component="span" className="menu-text-styles">
                Repertoire
              </Box>
            </Box>
          </>

          <Box
            component="button"
            type="button"
            onClick={() => navMenu('dashboard')}
            className="menu-hover-box"
            sx={{ position: 'relative' }}
            aria-current={selected == 'Dashboard' ? 'page' : undefined}
          >
            <Box
              component="span"
              sx={{
                position: 'absolute',
                top: '2px',
                right: '3px',
                borderRadius: '20px',
                width: '34px',
                fontWeight: '600',
                fontSize: '9px',
                lineHeight: '14px',
                textAlign: 'center',
                background: '#28856F',
                color: '#fff',
                pointerEvents: 'none',
              }}
            >
              NEW
            </Box>
            <DashboardIcon
              sx={{
                color: selected == 'Dashboard' ? '#6F87F1' : '#ADB1B7',
                fontSize: '32px',
              }}
            />
            <Box component="span" className="menu-text-styles">
              Dashboard
            </Box>
          </Box>

          {showHistoryOfPianoMusic && (
            <Box
              component="button"
              type="button"
              onClick={() => navMenu('history-of-piano-music')}
              className="menu-hover-box"
              aria-current={selected === 'History' ? 'page' : undefined}
            >
              <HistoryEduIcon
                sx={{
                  color: selected === 'History' ? '#6F87F1' : '#ADB1B7',
                  fontSize: '32px',
                }}
              />
              <Box component="span" className="menu-text-styles">
                History
              </Box>
            </Box>
          )}

          <Box
            component="button"
            type="button"
            onClick={() => navMenu('settings')}
            className="menu-hover-box"
            aria-current={selected == 'Settings' ? 'page' : undefined}
          >
            <PersonIcon
              sx={{
                color: selected == 'Settings' ? '#6F87F1' : '#ADB1B7',
                fontSize: '32px',
              }}
            />
            <Box component="span" className="menu-text-styles">
              Account
            </Box>
          </Box>

          <Box
            component="button"
            type="button"
            onClick={() => navMenu('faq')}
            className="menu-hover-box"
            aria-current={selected == 'FAQ' ? 'page' : undefined}
          >
            {selected == 'FAQ' ? (
              <img className="menu-img-styles" src={HelpIconSelected} />
            ) : (
              <img className="menu-img-styles" src={HelpIcon} />
            )}
            <Box component="span" className="menu-text-styles">
              Help
            </Box>
          </Box>

          <Box sx={{ ...iconContainerStyles1 }} className="menu-hover-box">
            <LogOutButton />
            <Box component="span" className="menu-text-styles" sx={{ marginTop: '0px' }}>
              Log Out
            </Box>
          </Box>
        </Box>
        <Box className="menu-sidebar-footer">
          <Fullscreen show_label={true} />
        </Box>
      </Box>
    </Box>
  );
};

// eslint-disable-next-line react-refresh/only-export-components -- shared styles
export const iconContainerStyles1 = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
};

const mobileItemSx = {
  borderRadius: 1,
  width: '100%',
  minHeight: 'clamp(44px, 12vh, 56px)',
  padding: 'clamp(2px, 1vh, 8px) 4px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  border: 0,
  backgroundColor: 'transparent',
  appearance: 'none',
  WebkitAppearance: 'none',
  WebkitTapHighlightColor: 'transparent',
  touchAction: 'manipulation',
  cursor: 'pointer',
  color: 'inherit',
  font: 'inherit',
  userSelect: 'none' as const,
  '&:active': {
    backgroundColor: 'rgba(202, 124, 98, 0.25)',
  },
  '&:focus-visible': {
    outline: '2px solid #6F87F1',
    outlineOffset: '2px',
  },
};

const mobileLabelSx = {
  marginTop: 'clamp(2px, 1vh, 8px)',
  fontSize: '13px',
  lineHeight: '16px',
};

const mobileFirstItemMargin = 'clamp(4px, 1.5vh, 14px)';
const mobileItemMargin = 'clamp(6px, 2vh, 20px)';

// Trigger button for the mobile menu drawer. `inline` lays it out in normal document
// flow (e.g. inside a page header); otherwise it floats fixed in the top-left corner.
export const MobileMenuButton = ({
  onClick,
  inline = false,
}: {
  onClick: () => void;
  inline?: boolean;
}) => {
  return (
    <IconButton
      onClick={onClick}
      sx={{
        ...(inline
          ? { flexShrink: 0 }
          : {
              position: 'fixed',
              zIndex: 1600,
              top: 'calc(env(safe-area-inset-top) + 17px)',
              left: '18px',
            }),
        color: '#CED0D4',
        backgroundColor: 'rgba(7, 20, 35, 0.6)',
        minWidth: 44,
        minHeight: 44,
        touchAction: 'manipulation',
        WebkitTapHighlightColor: 'transparent',
        '@media (hover: hover) and (pointer: fine)': {
          '&:hover': {
            backgroundColor: 'rgba(7, 20, 35, 0.85)',
          },
        },
        '&:active': {
          backgroundColor: 'rgba(7, 20, 35, 0.85)',
        },
      }}
      aria-label="Open menu"
    >
      <SidebarExpandIcon style={{ width: 24, height: 24, fill: 'currentColor' }} />
    </IconButton>
  );
};

const MobileMenuDrawer = ({
  selected,
  mobileOpen,
  setMobileOpen,
  hideMobileTrigger,
}: MenuProps) => {
  const navigate = useNavigate();
  const auth = useAuthWrapperContext();
  const [internalOpen, setInternalOpen] = useState(false);

  // Support both controlled (page-driven) and self-contained open state.
  const open = mobileOpen ?? internalOpen;
  const setOpen = setMobileOpen ?? setInternalOpen;

  const navMenu = (path: string) => {
    setOpen(false);
    if (path === 'faq') {
      window.open('https://museflow.ai/faq', '_blank');
    } else {
      navigate(`/${path}`);
    }
  };

  const logOut = () => {
    setOpen(false);
    auth?.logOut();
  };

  const opacityFor = (key: string) => (selected === key ? 0.9 : 0.85);
  const dashStroke = selected === 'Dashboard' ? '#6F87F1' : '#ADB1B7';

  return (
    <>
      {!hideMobileTrigger && <MobileMenuButton onClick={() => setOpen(true)} />}

      <Drawer
        anchor="left"
        open={open}
        onClose={() => setOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{ zIndex: 1700 }}
        PaperProps={{
          sx: {
            width: '162px',
            minWidth: '162px',
            maxWidth: '162px',
            height: '100dvh',
            maxHeight: '100dvh',
            boxSizing: 'border-box',
            overflowY: 'auto',
            backgroundColor: '#1D2F44',
            color: '#CED0D4',
            paddingTop: 'max(12px, env(safe-area-inset-top))',
            paddingBottom: 'max(12px, env(safe-area-inset-bottom))',
            display: 'flex',
            alignItems: 'stretch',
          },
        }}
      >
        <Box
          sx={{
            width: '108px',
            marginLeft: 'auto',
            marginRight: '12px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            pb: 1,
          }}
        >
          <Box
            component="button"
            type="button"
            onClick={() => navMenu('roadmap')}
            sx={{
              ...mobileItemSx,
              marginTop: mobileFirstItemMargin,
              opacity: opacityFor('Roadmap'),
            }}
            aria-current={selected === 'Roadmap' ? 'page' : undefined}
          >
            <img
              src={selected === 'Roadmap' ? MapIconSelected : MapIcon}
              style={{ width: '22px' }}
            />
            <Box component="span" className="menu-text-styles" sx={mobileLabelSx}>
              Roadmap
            </Box>
          </Box>

          <Box
            component="button"
            type="button"
            onClick={() => navMenu('repertoire')}
            sx={{ ...mobileItemSx, marginTop: mobileItemMargin, opacity: opacityFor('Repertoire') }}
            aria-current={selected === 'Repertoire' ? 'page' : undefined}
          >
            <img
              src={selected === 'Repertoire' ? RepertoireIconSelected : RepertoireIcon}
              style={{ width: '22px' }}
            />
            <Box component="span" className="menu-text-styles" sx={mobileLabelSx}>
              Repertoire
            </Box>
          </Box>

          <Box
            component="button"
            type="button"
            onClick={() => navMenu('dashboard')}
            sx={{
              ...mobileItemSx,
              marginTop: mobileItemMargin,
              opacity: opacityFor('Dashboard'),
              position: 'relative',
            }}
            aria-current={selected === 'Dashboard' ? 'page' : undefined}
          >
            <Box
              component="span"
              sx={{
                position: 'absolute',
                top: '4px',
                right: '8px',
                borderRadius: '20px',
                width: '34px',
                fontWeight: '600',
                fontSize: '9px',
                lineHeight: '14px',
                textAlign: 'center',
                background: '#28856F',
                color: '#fff',
                pointerEvents: 'none',
              }}
            >
              NEW
            </Box>
            <DashboardIcon sx={{ color: dashStroke, fontSize: '30px' }} />
            <Box component="span" className="menu-text-styles" sx={mobileLabelSx}>
              Dashboard
            </Box>
          </Box>

          <Box
            component="button"
            type="button"
            onClick={() => navMenu('settings')}
            sx={{ ...mobileItemSx, marginTop: mobileItemMargin, opacity: opacityFor('Settings') }}
            aria-current={selected === 'Settings' ? 'page' : undefined}
          >
            <PersonIcon
              sx={{
                color: selected === 'Settings' ? '#6F87F1' : '#ADB1B7',
                fontSize: '30px',
              }}
            />
            <Box component="span" className="menu-text-styles" sx={mobileLabelSx}>
              Account
            </Box>
          </Box>

          <Box
            component="button"
            type="button"
            onClick={() => navMenu('faq')}
            sx={{ ...mobileItemSx, marginTop: mobileItemMargin, opacity: opacityFor('FAQ') }}
            aria-current={selected === 'FAQ' ? 'page' : undefined}
          >
            <img src={selected === 'FAQ' ? HelpIconSelected : HelpIcon} style={{ width: '22px' }} />
            <Box component="span" className="menu-text-styles" sx={mobileLabelSx}>
              Help
            </Box>
          </Box>

          <Box
            component="button"
            type="button"
            onClick={logOut}
            sx={{ ...mobileItemSx, marginTop: mobileItemMargin, opacity: 0.85 }}
          >
            <LogoutIcon
              sx={{ color: 'rgb(255,255,255,0.8)', fontSize: '24px', transform: 'scaleX(-1)' }}
            />
            <Box component="span" className="menu-text-styles" sx={mobileLabelSx}>
              Log Out
            </Box>
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

const MenuLogoImg = () => {
  return (
    <svg xmlns="https://www.w3.org/2000/svg" width="34" height="34" viewBox="0 0 34 34" fill="none">
      <g clipPath="url(#clip0_2082_973)">
        <path
          d="M34.285 15.9397C33.2255 18.5376 30.5196 25.6453 25.9718 23.3905C25.5715 23.192 25.2296 22.9256 24.9373 22.6044C21.9082 19.2765 25.2484 9.71517 22.1086 9.71436C18.9688 9.71354 19.3212 19.2239 16.974 19.3219C14.6267 19.42 16.1288 12.7041 14.0888 11.7265C12.0488 10.749 11.4861 14.6972 8.82118 20.4425C6.15624 26.1879 2.03496 24.9059 -0.286377 20.4425"
          stroke="white"
          strokeWidth="1.70667"
          strokeLinecap="round"
        />
      </g>
      <rect x="1" y="1" width="32" height="32" rx="16" stroke="white" strokeWidth="1.70667" />
      <defs>
        <clipPath id="clip0_2082_973">
          <rect x="1" y="1" width="32" height="32" rx="16" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
};
